/* eslint-disable max-lines */
import { KeyboardEvent, useCallback, useRef, useState } from 'react';
import { LinkId } from '../../../AnalyticsContext/LinkId';
import { TrackingDataEvent } from '../../../AnalyticsContext/LinkTracking';
import { applyTrackingId } from '../../../AnalyticsContext/applyTrackingId';
import { renderLinkTrackingAttributes } from '../../../AnalyticsContext/renderLinkTrackingAttributes';
import { useEscapeKeyHandler } from '../../../hooks/keyboard/useEscapeKeyHandler';
import { useChangeEvent } from '../../../hooks/useChangeEvent';
import { useStatefulScrollLock } from '../../../hooks/useStatefulScrollLock';
import { generateSuggestionData } from '../LocationSearch/GenerateSuggestionData';
import { LocationSearchBar } from '../LocationSearch/LocationSearchBar';
import { NewSuggestions } from '../Suggestions/NewSuggestions/NewSuggestions';
import { SuggestionData } from '../Suggestions/types';
import { useSearchFilters } from '../filters/useSearchFilters';
import { FilterChoice } from '../types/FilterChoice';
import { LocationChoice, renderLocationChoice } from '../types/LocationChoice';
import { useSuggestionHandler } from '../useSuggestionHandler';
import { renderNewQuery } from './NewSearchBar';
import {
  Bar,
  BarLocation,
  SpecialtyIcon,
  InputSpecialty,
  SearchBarView,
  SearchIcon,
  LocationMarkerIcon,
  SearchIconBackground,
  ClearIcon,
} from './NewSearchBar.styles';

const DEFAULT_PLACEHOLDER_SPECIALTY = 'Service, practice or practitioner';

export interface Props {
  className?: string;
  specialtyFocused?: boolean;
  locationFocused?: boolean;
  setExpanded?: Setter<boolean>;
  setSpecialtyFocused?: Setter<boolean>;
  setLocationFocused?: Setter<boolean>;
  isSearchMultiLine?: boolean;
  setIsSearchMultiLine?: Setter<boolean>;
  onClear?: VoidFunction;
  placeholder?: string;
  locationValue: string;
  specialtyValue: string;
  setSpecialtyValue: Setter<string>;
  setLocationValue: Setter<string>;
  searchResultsSingleViewIsShowing: boolean;
  setSearchResultsSingleViewIsShowing: Setter<boolean>;
  setShowPlaceholder: Setter<boolean>;
}

export const SearchBarV2Desktop = ({
  setExpanded,
  setLocationFocused,
  setSpecialtyFocused,
  specialtyFocused,
  placeholder,
  locationFocused,
  className,
  searchResultsSingleViewIsShowing,
  setSearchResultsSingleViewIsShowing,
  setShowPlaceholder,
}: Props) => {
  const runSearchFromSuggestion = useSuggestionHandler();
  const { setLocked: setSearchScrollLock } = useStatefulScrollLock();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchResultRef = useRef<HTMLDivElement>(null);
  const observedPlaceholder = placeholder ?? DEFAULT_PLACEHOLDER_SPECIALTY;
  useEscapeKeyHandler(() => setSpecialtyFocused?.(false));
  const [filters] = useSearchFilters();

  const [locationValue, setLocationValue] = useState<LocationChoice | undefined>(filters.location);

  const [locationDisplayValue, setLocationDisplayValue] = useState(
    filters.location ? renderLocationChoice(filters.location) : '',
  );

  const [showLocationClearIcon, setShowLocationClearIcon] = useState(locationDisplayValue !== '');

  const [specialtyValue, setSpecialtyValue] = useState<FilterChoice | undefined>(filters);
  const [specialtyDisplayValue, setSpecialtyDisplayValue] = useState(renderNewQuery(filters) ?? '');

  const handleOnLocationSelect = (location: LocationChoice) => {
    setLocationFocused?.(false);
    setLocationDisplayValue(renderLocationChoice(location));
    setLocationValue(location);
    onSearchUpdate(undefined, location);
  };

  const onClickSearch = useCallback(() => {
    if (locationDisplayValue === '') {
      setLocationFocused?.(true);
      return;
    }

    runSearchFromSuggestion({
      type: 'TextSuggestion',
      filters: { text: specialtyDisplayValue ?? '' },
      id: `text-${specialtyDisplayValue}`,
    });

    setSpecialtyFocused?.(false);
    setExpanded?.(false);
    setSearchScrollLock(false);
  }, [
    runSearchFromSuggestion,
    setExpanded,
    setSearchScrollLock,
    setSpecialtyFocused,
    setLocationFocused,
    specialtyDisplayValue,
    locationDisplayValue,
  ]);

  const handleSpecialtyInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (!specialtyFocused) {
        setSpecialtyFocused?.(true);
      }

      if (event.key === 'Enter' || event.key === 'Tab') {
        searchResultRef?.current?.focus();
      }

      setSearchResultsSingleViewIsShowing(false);
    },
    [specialtyFocused, setSpecialtyFocused, setSearchResultsSingleViewIsShowing],
  );

  const handleLocationInputKeyDown = useCallback(() => {
    if (!locationFocused) {
      setLocationFocused?.(true);
    }
  }, [locationFocused, setLocationFocused]);

  const handleSpecialtyChange = useChangeEvent(setSpecialtyDisplayValue);

  const handleSpecialtyFocus = useCallback(() => {
    setSpecialtyFocused?.(true);
    setLocationFocused?.(false);
    setSpecialtyDisplayValue('');
  }, [setSpecialtyFocused, setLocationFocused, setSpecialtyDisplayValue]);

  const handleLocationFocus = useCallback(() => {
    setLocationFocused?.(true);
    setSpecialtyFocused?.(false);
    setLocationDisplayValue('');
  }, [setLocationFocused, setSpecialtyFocused, setLocationDisplayValue]);

  const setText = useCallback(
    (text: string) => {
      setSpecialtyDisplayValue(text);
    },
    [setSpecialtyDisplayValue],
  );

  const handleClearText = () => {
    if (specialtyFocused) {
      setSpecialtyDisplayValue('');
      setSpecialtyValue(undefined);
      setSpecialtyFocused?.(true);
    }

    if (locationFocused) {
      setLocationValue(undefined);
      setLocationDisplayValue('');
      setShowLocationClearIcon(false);
      setLocationFocused?.(true);
    }
  };

  const onSearchUpdate = (spec?: FilterChoice, loc?: LocationChoice) => {
    const suggestion = spec ?? specialtyValue;
    const location = loc ?? locationValue;

    const sugg = generateSuggestionData(
      { ...suggestion, onlineAppointments: filters.onlineAppointments ?? {} },
      location,
    );

    if (!location) {
      setSpecialtyFocused?.(false);
      setLocationFocused?.(true);
      setShowPlaceholder?.(false);
      return;
    }

    if (!suggestion || Object.keys(suggestion).length === 0) {
      setSpecialtyFocused?.(true);
      setLocationFocused?.(false);
      setShowPlaceholder?.(false);
      return;
    }

    if (!!suggestion && !!location) {
      runSearchFromSuggestion(sugg);
      setLocationFocused?.(false);
      setSpecialtyFocused?.(false);
      setShowPlaceholder(true);
    }
  };

  const localHandleOnSpecialtySuggestionCallback = (selection: SuggestionData) => {
    if (selection.type === 'PracticeSuggestion' || selection.type === 'PractitionerSuggestion') {
      setLocationFocused?.(false);
      setSpecialtyFocused?.(false);
      setShowPlaceholder?.(true);

      return;
    }

    setSpecialtyValue(
      selection.type === 'FilterSuggestion' || selection.type === 'TextSuggestion'
        ? selection.filters
        : undefined,
    );

    onSearchUpdate(
      selection.type === 'FilterSuggestion' || selection.type === 'TextSuggestion'
        ? selection.filters
        : undefined,
    );
  };

  return (
    <SearchBarView className={className} isSearchMultiLine={false}>
      <Bar isSearchMultiLine={false}>
        <SpecialtyIcon />
        <InputSpecialty
          ref={searchInputRef}
          {...applyTrackingId(LinkId.HEADER_SEARCH_BAR)}
          onChange={handleSpecialtyChange}
          placeholder={observedPlaceholder}
          onFocus={handleSpecialtyFocus}
          value={specialtyDisplayValue}
          onKeyDown={handleSpecialtyInputKeyDown}
          data-testid="searchBarInput"
          data-link-id="Search bar specialty selection"
        />
        {specialtyDisplayValue !== '' && (
          <ClearIcon isSpecialty={true} isHidden={!specialtyFocused} onClick={handleClearText} />
        )}
      </Bar>
      <BarLocation isSearchMultiLine={false}>
        <LocationMarkerIcon isSearchMultiLine={false} />
        <LocationSearchBar
          locationFocused={locationFocused}
          onFocus={handleLocationFocus}
          value={locationDisplayValue}
          setValue={setLocationDisplayValue}
          onLocationSelected={handleOnLocationSelect}
          onKeyDown={handleLocationInputKeyDown}
          onLocationTextChange={value => setShowLocationClearIcon(value !== '')}
          setShowPlaceholder={setShowPlaceholder}
        />
        {showLocationClearIcon && (
          <ClearIcon isSpecialty={false} isHidden={!locationFocused} onClick={handleClearText} />
        )}
        <SearchIconBackground
          onClick={onClickSearch}
          {...renderLinkTrackingAttributes({ event: TrackingDataEvent.SEARCH_BAR_SEARCH_ICON })}
        >
          <SearchIcon name="Search" />
        </SearchIconBackground>
      </BarLocation>
      {specialtyFocused && (
        <NewSuggestions
          setValue={setText}
          searchTerm={specialtyDisplayValue}
          handleOnSpecialtySuggestionCallback={localHandleOnSpecialtySuggestionCallback}
          enabledFilterOffset={false}
          searchResultsSingleViewIsShowing={searchResultsSingleViewIsShowing}
          setSearchResultsSingleViewIsShowing={setSearchResultsSingleViewIsShowing}
          ref={searchResultRef}
          setLocationFocused={setLocationFocused}
          locationValue={locationDisplayValue}
          setShowPlaceholder={setShowPlaceholder}
          setSpecialtyFocused={setSpecialtyFocused}
        />
      )}
    </SearchBarView>
  );
};
